import React, { useState } from 'react';
import { Icon } from '../../icons';
import { EquipmentCategory, EquipmentCategoryCreate, EquipmentCategoryUpdate } from '../../types/equipmentCategory';
import { IconToken } from '../../icons/types';
import { IconSelect } from '../atoms';

interface CategoryFormProps {
  initialData?: EquipmentCategory;
  onSubmit: (data: EquipmentCategoryCreate | EquipmentCategoryUpdate) => void;
  onCancel: () => void;
  isEdit?: boolean;
}

export const CategoryForm: React.FC<CategoryFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false,
}) => {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [icon, setIcon] = useState(initialData?.icon || 'default');
  const [error, setError] = useState<string | null>(null);

  // Cerrar con Escape
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onCancel]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    setError(null);
    onSubmit({ name, description, icon });
  };

  // Lista de iconos disponibles como opciones para IconSelect
  const iconOptions = [
    { value: 'default', label: 'Default', icon: 'default' },
    { value: 'magnet', label: 'Magnet', icon: 'magnet' },
    { value: 'accessibility', label: 'Accessibility', icon: 'accessibility' },
    { value: 'info', label: 'Info', icon: 'info' },
    { value: 'error', label: 'Error', icon: 'error' },
    { value: 'check', label: 'Check', icon: 'check' },
    { value: 'whatsapp', label: 'Whatsapp', icon: 'whatsapp' },
    { value: 'rotateccw', label: 'RotateCcw', icon: 'rotateccw' },
    { value: 'waves', label: 'Waves', icon: 'waves' },
    { value: 'snowflake', label: 'Snowflake', icon: 'snowflake' },
    { value: 'flame', label: 'Flame', icon: 'flame' },
    { value: 'boxes', label: 'Boxes', icon: 'boxes' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-blue-500/80 bg-opacity-50 animate-in fade-in-0 duration-300"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-300"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="mb-4 text-xl font-bold">{isEdit ? 'Editar Categoría' : 'Crear Categoría'}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nombre *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Descripción</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Icono</label>
            <IconSelect
              value={icon as IconToken}
              options={iconOptions}
              onChange={setIcon}
              className="w-full"
            />
            <div className="mt-2">
              <Icon name={icon} size={28} />
            </div>
          </div>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex pt-4 space-x-3 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-300 rounded-lg hover:bg-gray-400"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              {isEdit ? 'Guardar Cambios' : 'Crear Categoría'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
